
import {
  computed,
  defineComponent,
  inject, nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  ICase, IFolder, IMedia, IReactiveSharePopUp, ISharedLable,
} from '@/types/interfaces';
import {
  BOTTOM_POPUP_TYPES,
  NAVIGATION_PAGES,
  PHOTO_VIEW_LAYOUT, PHOTO_VIEW_TYPE, SHARE_ACCESS, SHARE_SETTINGS,
  SHARE_TYPES,
  UPLOAD_PLACES,
} from '@/types/enums';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '@/modules/drive/store/driveStore';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import UseUpload from '@/composables/UseUpload';
// eslint-disable-next-line import/no-cycle
import UploadService from '@/services/UploadService';
import AppHeader from '@/components/AppHeader.vue';
import AppSearch from '@/components/UI/AppSearch.vue';
import AppNavigation from '@/components/AppNavigation.vue';
import AppAddButton from '@/components/AppAddButton.vue';
import AppFileView from '@/components/AppFileView.vue';
import AppLoader from '@/components/UI/AppLoader.vue';
import AppProgress from '@/components/UI/AppProgress.vue';
import AppCreateFolderPopUp from '@/components/AppCreateFolderPopUp.vue';
import uploadPhotosPopUp from '@/components/BottomPopUp/PopUpTypes/Upload/UploadPhotosPopUp.vue';
import AppSkeletonCaseEdit from '@/components/skeletons/AppSkeletonCaseEdit.vue';
import AppPhotoView from '@/components/AppPhotoView.vue';
import AppMixedSelector from '@/components/AppMixedSelector.vue';
import AppPhotoSelector from '@/components/AppPhotoSelector.vue';
import AppCaseSelector from '@/components/AppCaseSelector.vue';
import SharePopUp from '@/components/BottomPopUp/PopUpTypes/Share/SharePopUp.vue';
import CaseSettingsPopUp from '@/components/BottomPopUp/PopUpTypes/Settings/CaseSettingsPopUp.vue';
import MoveToPopUp from '@/components/BottomPopUp/PopUpTypes/MoveTo/MoveToPopUp.vue';
import DriveSharedBar from '@/modules/drive/components/DriveSharedBar.vue';
import AppMediaView from '@/components/AppMediaView/AppMediaView.vue';
import DriveMediaView from '@/modules/drive/components/desktop/mediaview/DriveMediaView.vue';
import DriveDeleteConfirm from '@/modules/drive/components/DriveDeleteConfirm.vue';
import AppQuestionPopUp from '@/components/AppQuestionPopUp.vue';
import DriveUploadAllMenu from '@/modules/drive/components/desktop/DriveUploadAllMenu.vue';
import DriveAsideMainMenu from '@/modules/drive/components/desktop/DriveAsideMainMenu.vue';
import { convertBmpInFileList, bmpConvertorToFile } from '@/helpers/bmpConvertor';
// eslint-disable-next-line import/no-cycle
import DriveService from '@/modules/drive/services/DriveService';
import AppReorderPhoto from '@/components/AppReorderPhoto.vue';
// eslint-disable-next-line import/no-cycle
import useDriveBooleans from '@/composables/UseDriveBooleans';
// eslint-disable-next-line import/no-cycle
import useHeaderBooleans from '@/composables/UseHeaderBooleans';
// eslint-disable-next-line import/no-cycle
import AuthService from '@/modules/auth/services/AuthService';
import DesktopHeader from '@/components/desktop/DesktopHeader.vue';

export default defineComponent({
  components: {
    DesktopHeader,
    AppReorderPhoto,
    AppHeader,
    AppSearch,
    AppNavigation,
    AppAddButton,
    AppLoader,
    AppFileView,
    AppProgress,
    AppCreateFolderPopUp,
    AppSkeletonCaseEdit,
    AppPhotoView,
    AppMixedSelector,
    AppPhotoSelector,
    AppCaseSelector,
    SharePopUp,
    CaseSettingsPopUp,
    MoveToPopUp,
    DriveSharedBar,
    AppMediaView,
    DriveMediaView,
    DriveDeleteConfirm,
    AppQuestionPopUp,
    DriveUploadAllMenu,
    DriveAsideMainMenu,
  },
  setup() {
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;

    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const route = useRoute();
    const router = useRouter();

    const {
      isDrive, isCaseFolder, isCaseView, isDriveFolder, isNetwork,
      hideAppButton, isAddMedia, navigationData,
    } = useDriveBooleans();

    const {
      isSelector, hideHeaderSearch, customTitle, topRightText, isDoneDisabled,
      isBackArrow, actionBar,
    } = useHeaderBooleans();

    function addCase() {
      const path = '/drive/case/create';
      router.push(path);
      // mainStore.openCreateCasePopUp();
    }

    const { onUploadUpdate, uploadInfo } = UseUpload();

    const sharedPopUp: IReactiveSharePopUp = reactive({
      item: null,
      type: null,
    });

    const isDeleteConfirm = ref<boolean>(false);
    const isMoveToCasePopUp = ref<boolean>(false);
    const isCaseSettingsPopUp = ref<boolean>(false);
    const isQuestionPopUp = ref<boolean>(false);
    const isAddMenuDesktop = ref<boolean>(false);

    const isSaving = ref<string>('');
    const isProgress = ref<boolean>(false);
    const isCaseCreating = ref<boolean>(false);
    const isCaseCopying = ref<boolean>(false);
    const percent = ref<number>(0);
    const fileInput = ref<null|HTMLInputElement>(null);
    const isUserCasesFolder = computed(() => mainStore.userSettings.accountSystemSettings.userCasesFolderId === route.params.id);

    const currentPage = computed(() => (route.meta.navigationPage));
    // const backText = computed(() => $localize(route.meta.backText));
    const hideBottomNav = computed(() => (route.meta.hideBottomNav));

    const fullName = computed(() => `${mainStore.user.firstName} ${mainStore.user.lastName}`); // header desktop
    const photoUrl = computed(() => { // header desktop
      if (mainStore.user.photo?.url) {
        return lowResUrlByViewTypeByUrl(mainStore.user.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });

    function navigateTo(rout: string) {
      router.push(rout);
    }

    function openSelector() {
      if (currentPage.value === NAVIGATION_PAGES.DRIVE_PHOTOS) {
        driveStore.isPhotoSelector = true;
      } else if (currentPage.value === NAVIGATION_PAGES.DRIVE_FILES || currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER) {
        driveStore.isMixedSelector = true;
      } else if (currentPage.value === NAVIGATION_PAGES.DRIVE_CASES) {
        driveStore.isCaseSelector = true;
      }
    }

    function onUpdateRequest(per: number): void {
      percent.value = per;
    }

    function saveCase(isUpdate = true) {
      if (isDoneDisabled.value) {
        return;
      }
      if (isUpdate) {
        isProgress.value = true;
      } else {
        isCaseCreating.value = true;
      }

      driveStore.updateCase(onUpdateRequest)
        .then((response) => {
          if (isUpdate) {
            driveStore.resetCurrentCase();
            mainStore.inputCaseTags = [];
            router.push(`/drive/case/my/${response.results.data.id}/view/`).then(() => {
              if (isUpdate) {
                isProgress.value = false;
              } else {
                isCaseCreating.value = false;
              }
              percent.value = 0;
              // NotificationService.notifySuccess();
            });
          } else {
            // if (!route.query.caseId) NotificationService.notifySuccess(`Case ${driveStore.bufferCurrentCaseTitle} has been created.`);
            driveStore.resetCurrentCase();
            router.push(`/drive/case/${response.results.data.id}/edit`).then(() => {
              if (isUpdate) {
                isProgress.value = false;
              } else {
                isCaseCreating.value = false;
              }
              percent.value = 0;
              // mainStore.closeCreateCasePopUp();
            });
          }

          setTimeout(() => {
            if (isUpdate) {
              isProgress.value = false;
            } else {
              isCaseCreating.value = false;
            }
            percent.value = 0;
          }, 100);
        })
        .catch((e) => {
          const errorData = e?.response?.data;
          if (errorData) {
            if (errorData.errorKey === 'CASE_NAME_EXISTS') {
              driveStore.caseTitleValidation = 'Name is already taken';
            } else {
              driveStore.caseTitleValidation = 'Something gone wrong';
            }
          } else {
            driveStore.caseTitleValidation = 'Something gone wrong';
          }
          if (isUpdate) {
            isProgress.value = false;
          } else {
            isCaseCreating.value = false;
          }
          percent.value = 0;
        });
    }

    function deleteCase() {
      // isSaving.value = 'Deleting Case...';
      isQuestionPopUp.value = false;
      driveStore.deleteCase();
      if (currentPage.value === NAVIGATION_PAGES.CASE_VIEW) {
        // router.back();
        router.push('/drive/my/cases');
      }
      isCaseSettingsPopUp.value = false;
      // isDeleteConfirm.value = false;
      // .then(() => {
      //   router.push('/drive').then(() => {
      //     isSaving.value = '';
      //     isCaseSettingsPopUp.value = false;
      //     NotificationService.notifySuccess();
      //   });
      // })
      // .catch(() => {
      //   isSaving.value = '';
      // });
    }

    function goToCase(caseId: string) {
      router.push(`/drive/case/my/${caseId}/view/`).then(() => {
        driveStore.fullscreenFile = undefined;
        driveStore.fullscreenPhoto = undefined;
        mainStore.BottomPopUpType = null;
      });
    }

    function saveCaseAsCopy() {
      isCaseCopying.value = true;
      isCaseSettingsPopUp.value = false;
      driveStore.saveCaseAsCopy().then((response) => {
        if (response.success) {
          isCaseCopying.value = false;
          goToCase(response.results.data.id!);
        }
      });
    }

    function removeCaseFromSharing() {
      const { id } = route.params;
      if (id) {
        const selectedCases = {
          ids: [id as string],
          accessType: SHARE_ACCESS.SPECIFIC_PEOPLE,
          target: '',
        };
        driveStore.networkCases.data = driveStore.networkCases.data.filter((selectedCase) => selectedCase.id !== id);
        DriveService.removeFromSharing(selectedCases).then(() => {
          driveStore.fetchCases().then(() => {
            isCaseSettingsPopUp.value = false;
            router.go(-1);
          });
        });
      }
    }

    function doneAction(): void {
      switch (currentPage.value) {
        case NAVIGATION_PAGES.CASE_CREATE:
          saveCase(false);
          break;
        default:
          break;
      }
    }

    function backAction(): void {
      switch (currentPage.value) {
        case NAVIGATION_PAGES.CASE_CREATE:
          if (route.query.caseId) {
            isDeleteConfirm.value = true;
          } else {
            router.back();
          }
          break;
        case NAVIGATION_PAGES.DRIVE_FOLDER:
          if (!isNetwork.value) {
            driveStore.folderFileAmount.pop();
            if (driveStore.currentFolder?.upperFolderId) {
              router.push(`/drive/folder/${driveStore.currentFolder.upperFolderId}`);
            } else if (driveStore.currentFolder?.upperFolderId === null) {
              router.push('/drive');
            } else {
              router.back();
            }
          } else if (isNetwork.value) {
            if (driveStore.currentFolder?.upperFolderId) {
              router.push(`/drive/network/folder/${driveStore.currentFolder.upperFolderId}`);
            } else {
              router.back();
            }
          }
          break;
        case NAVIGATION_PAGES.DRIVE_CASES:
          router.push('/drive');
          break;
        case NAVIGATION_PAGES.CASE_VIEW:
          if (router.options.history.state.back === `/drive/case/${driveStore.currentCase.id}/edit`) {
            console.log(router.options.history.state.back);
            router.go(-3); // also we have router guard to detect if we trapped into case creation page
          } else if (router.options.history.state.back === '/drive/my/files') {
            router.go(-1);
          } else if (router.options?.history?.state?.back !== null && ((router.options.history.state.back) as string)?.includes('/view/')) {
            router.push('/drive/my/cases');
          } else if (router.options.history.state.back && !NAVIGATION_PAGES.PROFILE_FOREIGN) { // CASE-1515 Double click on Back button bug
            const queryFromPhoto = route.query.fromPhoto;
            const st = driveStore.search;
            router.push(router.options.history.state.back as string).then(() => {
              if (queryFromPhoto) {
                driveStore.appPhotoViewMedia = driveStore.photos.data.find((ph) => ph.id === queryFromPhoto);
              }
              if (st.length) {
                nextTick(() => {
                  driveStore.search = st;
                });
              }
            });
          } else {
            router.back();
          }
          break;
        default:
          if ((router.options.history.state.back as string)?.includes('?st=')) { // CASE-1600 E-mail link navigation - "back" issue
            router.push('/');
          } else {
            router.back();
          }
          break;
      }
    }

    function openBottomPopUp(type: BOTTOM_POPUP_TYPES) {
      mainStore.BottomPopUpType = type;
    }

    const loaderText = computed(() => {
      if (isSaving.value) {
        return isSaving.value;
      }
      return '';
    });

    async function uploadMedia(event: Event) {
      const fileList: FileList | null = (event.target as HTMLInputElement).files;

      if (fileList) {
        const updatedFileList = await convertBmpInFileList(fileList);
        const fileUploads = UploadService.toFileUploads(updatedFileList);
        UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.PHOTO });
      }

      fileInput.value!.value = '';
    }

    function closeFullScreen() {
      driveStore.appPhotoViewMedia = undefined;
      driveStore.fullscreenPhoto = undefined;
      driveStore.fullscreenFile = undefined;
    }

    function onSwitchPhoto(direction: string): void {
      let idx = driveStore.photoList.findIndex((photo) => photo.id === driveStore.fullscreenPhoto?.id);
      if (direction === 'RIGHT') {
        idx = (idx + 1) % driveStore.photoList.length;
        // driveStore.fullscreenPhoto = driveStore.photoList[idx];
      } else if (direction === 'LEFT') {
        idx = idx === 0 ? driveStore.photoList.length - 1 : idx - 1;
        // driveStore.fullscreenPhoto = driveStore.photoList[idx];
      }
    }

    function onAddButtonClick() {
      if (currentPage.value === NAVIGATION_PAGES.DRIVE) {
        openBottomPopUp(BOTTOM_POPUP_TYPES.UPLOAD_ALL);
      } else if (currentPage.value === NAVIGATION_PAGES.DRIVE_FILES) {
        openBottomPopUp(BOTTOM_POPUP_TYPES.UPLOAD_ALL);
      } else if (currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER) {
        openBottomPopUp(BOTTOM_POPUP_TYPES.UPLOAD_ALL);
      } else if (currentPage.value === NAVIGATION_PAGES.CASE_VIEW) {
        openBottomPopUp(BOTTOM_POPUP_TYPES.UPLOAD_CASE);
      } else {
        router.push('/drive/case/create');
        // mainStore.openCreateCasePopUp();
      }
    }

    function onAddButtonClickDesktop() {
      if (currentPage.value === NAVIGATION_PAGES.DRIVE) {
        isAddMenuDesktop.value = !isAddMenuDesktop.value;
      } else if (currentPage.value === NAVIGATION_PAGES.DRIVE_FILES) {
        isAddMenuDesktop.value = !isAddMenuDesktop.value;
      } else if (currentPage.value === NAVIGATION_PAGES.DRIVE_CASES) {
        isAddMenuDesktop.value = !isAddMenuDesktop.value;
      } else if (currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER) {
        isAddMenuDesktop.value = !isAddMenuDesktop.value;
      } else if (currentPage.value === NAVIGATION_PAGES.CASE_VIEW) {
        openBottomPopUp(BOTTOM_POPUP_TYPES.UPLOAD_CASE);
      } else {
        // router.push('/drive/case/create');
        // mainStore.openCreateCasePopUp();
      }
    }

    function openSharePopUp(item: IMedia | IFolder | ICase, type: SHARE_TYPES) {
      sharedPopUp.item = item;
      sharedPopUp.type = type;
    }

    function closeSharePopUp() {
      sharedPopUp.item = null;
      sharedPopUp.type = null;
    }

    function removeSharedItemFromStore() {
      closeSharePopUp();
      driveStore.fullscreenFile = undefined;
      driveStore.fullscreenPhoto = undefined;
      driveStore.appPhotoViewMedia = undefined;
    }

    function setItemShared() {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // sharedPopUp.item!.shared = true
    }

    function setSharedLabels(labels: ISharedLable[]) {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // sharedPopUp.item!.sharedLabels = labels;
    }

    /** Don't touch anymore this logout or ping me before Grygoriy Lyashenko */
    function logOut() {
      AuthService.logout().then((response) => {
        if (response.success) {
          router.push('/');
        }
      });
    }

    function openMovePopUp() {
      isCaseSettingsPopUp.value = false;
      isMoveToCasePopUp.value = true;
    }

    function openQuestionPopUp() {
      isCaseSettingsPopUp.value = false;
      isQuestionPopUp.value = true;
    }

    function updateLocation(folderId: string | null) {
      driveStore.currentCase!.folderId = folderId;
    }

    function cancelUpload() {
      UploadService.isAbortUpload = true;
    }

    const reorderPhotoView = ref(false);
    function openReorderPhoto() {
      isCaseSettingsPopUp.value = false;
      reorderPhotoView.value = true;
    }

    onMounted(() => {
      // Don't remove now
      // fetchContacts(); - commented by Andrii, we prefetch this data now in UserService
      // TODO!!!!
      // update to fetch contacts on initial load and then only in contacts view, contacts view also required update after actions
      // all contacts should be part of User object in mainStore or separate object, but still in mainStore, it is main information about account
    });

    onUnmounted(() => {
      driveStore.search = '';
    });

    return {
      mainStore,
      fileInput,
      navigationData,
      hideAppButton,
      hideBottomNav,
      driveStore,
      customTitle,
      currentPage,
      NAVIGATION_PAGES,
      isSaving,
      // backText,
      isBackArrow,
      hideHeaderSearch,
      isAddMedia,
      isProgress,
      isCaseCreating,
      isCaseCopying,
      percent,
      isNetwork,
      topRightText,
      isDoneDisabled,
      isSelector,
      // isCaseFolder,
      // isFileFolder,
      sharedPopUp,
      isCaseSettingsPopUp,
      isMoveToCasePopUp,
      isDeleteConfirm,
      loaderText,
      uploadInfo,
      isQuestionPopUp,
      isAddMenuDesktop,
      fullName,
      photoUrl,
      actionBar,
      cancelUpload,
      openSelector,
      doneAction,
      backAction,
      openBottomPopUp,
      uploadMedia,
      closeFullScreen,
      onSwitchPhoto,
      onAddButtonClick,
      onAddButtonClickDesktop,
      openSharePopUp,
      closeSharePopUp,
      setSharedLabels,
      openMovePopUp,
      openQuestionPopUp,
      deleteCase,
      saveCaseAsCopy,
      removeCaseFromSharing,
      updateLocation,
      removeSharedItemFromStore,
      BOTTOM_POPUP_TYPES,
      SHARE_TYPES,
      PHOTO_VIEW_LAYOUT,
      setItemShared,
      isDrive,
      isDriveFolder,
      isCaseView,
      router,
      route,
      logOut,
      isCaseFolder,
      addCase,

      reorderPhotoView,
      openReorderPhoto,
      navigateTo,
      isUserCasesFolder,
    };
  },
});
